import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from "react-redux";
import {
    Button,
    Card,
    Col,
    Form,
    Modal,
    OverlayTrigger,
    Row,
    Table,
    Toast,
    Tooltip,
    Alert
} from 'react-bootstrap'
import classes from "./ProgramsAddEdit.module.css";
import { saveEventSnapshot, updateEventSnapshot } from "./ProgramsAddEditSlice";
import { Link, useHistory } from 'react-router-dom';
// import queryString from 'query-string';
import DayBox from './DayBox';
import {
    startProgramBuild,
    addEventToProgram,
    getAllProgramEvents,
    assignProgramToClients,
    editProgramEvent,
    deleteEvent,
    addMessageToProgram,
    getFavoriteDetails,
    getMessageTemplates,
    editProgramBuildLength,
    editProgramBuildName,
    editProgramBuildNotes,
    deleteAllEvents,
    getAllPrograms,
    updateProgramMessage,
    deleteProgramMessage,
    deleteProgramMessageGroup,
    getEventsForBuildingProgram
} from "./ProgramsAddEditSlice";
import {
    addProgramAssignments,
    setCopyModal,
    getPrograms
} from '../programs/programsSlice'
import { selectTrainerParam } from '../users/usersSlice'
import { getEventList } from "../eventsView/eventsViewSlice"
import AssignModal from '../programs/AssignModal/AssignModal'
import CopyModal from '../programs/CopyModal/CopyModal'
import Spinner from "react-bootstrap/Spinner";
import { extractEventParams, envAwarePost } from '../../util/util';
import useApiStatusEnvAwarePost from '../../hooks/useApiStatusEnvAwarePost'
import { setShow } from '../MessageCenter/messageCenterSlice';
import Select from 'react-select'
import { useFavoriteEvents } from './hooks'
import { capitalize } from 'lodash';
import EventDetails from '../EventDetails/EventDetails';
import { selectFilteredEvents } from '../../state/events';

const eventTypes = ['workout', 'task', 'nutrition']
const generateWeeklyDays = (weekDays, fromDay, toDay) => {
    //create base value to use when accumulating result
    const result = Array.from(new Set(weekDays))
    let weekIdx = 1
    while (result[result.length - 1] <= toDay) {
        weekDays.forEach(d => {
            result.push(d + weekIdx * 7)
        })
        weekIdx += 1
    }
    return result.filter(d => fromDay <= d && d <= toDay)
}

const UpdateAssignmentModal = ({ show, toggleShow, id }) => {
    const user = useSelector(state => state.users.current_user)
    const isChain = user.account_type === 'chain'
    const dispatch = useDispatch()
    const [assignments, setAssignments] = useState([])
    const [assignmentsStatus, setAssignmentsStatus] = useState('pending')
    const [updateStatus, setUpdateStatus] = useState('idle')
    //mode one of ['init','confirm']
    const [mode, setMode] = useState('init')

    const assignmentsOptions = useMemo(() => assignments.map(a => ({
        label: a.assigned_user_name,
        value: a.id
    })), [assignments])

    const [selectedOptions, setSelectedOptions] = useState([])
    const [applyToAll, setApplyToAll] = useState(false)
    const statusPost = useApiStatusEnvAwarePost()
    useEffect(() => {
        setSelectedOptions([])
        setApplyToAll(false)
        if (!id || isChain) return
        setAssignmentsStatus('pending')
        envAwarePost('api/programs/api/', {
            action: 'getProgramAssignments',
            program_id: id
        }).then(res => {
            setAssignmentsStatus('idle')
            if (res.success === '1') {
                setAssignments(res.data)
            } else {
                alert(res.message)
            }
        })
    }, [id, show])

    const handleApplyUpdates = () => {
        if (!window.confirm('Are you sure that you want to apply edits to this program to all selected assignments?')) return
        setUpdateStatus('pending')
        const postData = {
            action: 'updateProgramAssignments',
            program_id: id
        }
        if (applyToAll) {
            postData.apply_to_all = 1;
        } else {
            postData.assignment_ids = selectedOptions.map(o => o.value)
        }
        statusPost('api/programs/api/', postData, 'program assignments', 'edit').then(() => {
            setUpdateStatus('idle')
            toggleShow()
        })
    }

    return (
        <Modal show={show} onHide={toggleShow} style={{ padding: "20px" }} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Update program assignments</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    assignmentsStatus === 'pending' ?
                        <Spinner animation="border" size="sm" /> :
                        <>
                            <div className="d-flex">
                                <Select
                                    isDisabled={applyToAll}
                                    className="w-100"
                                    placeholder={applyToAll ? 'Updating all assignments' : 'Select'}
                                    value={applyToAll ? [] : selectedOptions}
                                    options={assignmentsOptions}
                                    onChange={setSelectedOptions}
                                    isMulti
                                />
                                <Button
                                    variant={applyToAll ? "primary" : "outline-primary"}
                                    onClick={() => { setApplyToAll(!applyToAll) }}
                                    className="ml-1"
                                >
                                    All
                                </Button>
                            </div>
                            <Button
                                disabled={(!applyToAll && !selectedOptions.length) || (updateStatus === 'pending')}
                                onClick={handleApplyUpdates}
                                className="mt-2"
                                variant="outline-success"
                                block
                            >
                                <span className="mr-1">Apply program edits to selected client(s)</span>
                                {updateStatus === 'pending' && <Spinner animation="border" size="sm" />}
                            </Button>
                            <small className="text-danger d-block text-center mt-2">
                                Warning: edits applied to the selected program assignments CANNOT BE UNDONE.
                            </small>
                        </>
                }
            </Modal.Body>
        </Modal>
    )
}

const ProgramsEdit = ({ location }) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const urlParams = new URLSearchParams(location.search)
    const id = urlParams.get('program')

    const trainerParam = useSelector(selectTrainerParam)
    const user = useSelector(state => state.users.current_user)
    const programEventsState = useSelector((state) => state.programEvents)
    const eventWorkouts = useSelector((state) => state.programEvents?.programEventsData?.workouts)
    const eventNutritions = useSelector((state) => state.programEvents?.programEventsData?.nutritions)
    const eventTasks = useSelector((state) => state.programEvents?.programEventsData?.tasks)
    const eventMessages = useSelector((state) => state.programEvents?.programEventsData?.messages)
    const favoriteDetails = useSelector((state) => state.programEvents?.favoriteEvents ? state.programEvents.favoriteEvents[0] : null)
    const templates = useSelector((state) => state.programEvents?.messageTemplates)
    const allPrograms = useSelector((state) => state.programEvents?.allPrograms)
    const eventData = useSelector((state) => state.programEvents?.programEventsData)
    const clients = useSelector((state) => state.users.clients.entities)
    const clientsArr = Object.entries(clients)
    const [favoriteDetailsLoading, setFavoriteDetailsLoading] = useState(false);
    const eventSnapshot = useSelector(state => state.programEvents.eventSnapshot)
    const program = useSelector(state => state.programs.programs.entities[id])
    const isOwner = user?.user_id_number == program?.user_id_number
    const [showAddEvent, setShowAddEvent] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const isChain = user.account_type === 'chain'

    const handleCloseAddEvent = () => {
        setShowAddEvent(false)
        setRepeat('none')
        setEventID("0")
        setFilteredValue(null)
    }

    const handleShowAddEvent = () => setShowAddEvent(true)

    const [showAssign, setShowAssign] = useState(false)

    const handleCloseAssign = () => setShowAssign(false)

    const handleShowAssign = () => {
        dispatch(addProgramAssignments(parseInt(id)))
    }

    const [showEdit, setShowEdit] = useState(false)

    const handleCloseEdit = () => {
        setShowEdit(false)
        setEventID("0")
        setRepeat('none')
        setShowDetails(false);
        setFilteredValue(null)
    }

    const handleShowEdit = (e) => {
        e.stopPropagation();
        setShowEdit(true);
    }

    const [daysArray, setDaysArray] = useState([])
    const [programLength, setProgramLength] = useState("")
    const [programName, setProgramName] = useState("")
    const [programNotes, setProgramNotes] = useState("")
    const [eventType, setEventType] = useState("workout")
    const [programID, setProgramID] = useState(id)
    const {
        workouts: favoriteWorkouts,
        tasks: favoriteTasks,
        nutrition: favoriteNutritions
    } = useFavoriteEvents()
    const [daySelected, setDaySelected] = useState("")
    const [eventDays, setEventDays] = useState("")
    const [eventID, setEventID] = useState("0")
    const [programWorkouts, setProgramWorkouts] = useState([])
    const [programNutritions, setProgramNutritions] = useState([])
    const [programTasks, setProgramTasks] = useState([])
    const [startBuilding, setStartBuilding] = useState(false)
    const [clientList, setClientList] = useState([])
    const [startDate, setStartDate] = useState([])
    const [repFreq, setRepFreq] = useState("1")
    const [customDayString, setCustomDayString] = useState("");
    const [untilDay, setUntilDay] = useState("");
    const [programDays, setProgramDays] = useState([])
    const [programEventsID, setProgramEventsID] = useState("")
    const [day, setDay] = useState("")
    const [sendAt, setSendAt] = useState("")
    const [is24Hour, setIs24Hour] = useState(0)
    const [messageBody, setMessageBody] = useState("")
    const [repeat, setRepeat] = useState("none")
    const [showDetails, setShowDetails] = useState(false)
    const [showMessageTemplate, setShowMessageTemplate] = useState(false)
    const [checkboxArr, setCheckboxArr] = useState([])
    const [programBuilt, setProgramBuilt] = useState(true)
    const [eventInEditor, setEventInEditor] = useState(null)
    const [alertMessage, setAlertMessage] = useState(false)
    const [dragInfo, setDragInfo] = useState(0)

    const dayRange = (start, end, step) => {
        return Array.from(Array.from(Array(Math.ceil((end - start + 1) / step)).keys()), x => start + x * step);
    }

    const [showMainToast, setShowMainToast] = useState(false)
    const [showModalToast, setShowModalToast] = useState(false)
    const [showAssignToast, setShowAssignToast] = useState(true)

    const [status, setStatus] = useState('pending')

    const [favoritesFiltered, setFavoritesFiltered] = useState([])
    // favorites being displayed depends on eventType (workouts/tasks/nutrition) 
    useEffect(() => {
        const favorites =
            // if eventType = workout
            eventType === "workout" ? favoriteWorkouts :
                // if eventType = nutrition
                eventType === "nutrition" ? favoriteNutritions :
                    // if eventType = task
                    eventType === "task" ? favoriteTasks :
                        [];


        setFavoritesFiltered(favorites)

    }, [eventType, favoriteWorkouts, favoriteNutritions, favoriteTasks])


    useEffect(() => {
        setStatus('pending')
        dispatch(getPrograms()).then(res => {
            setStatus('idle')
        })
        dispatch(getAllPrograms())
            .then((res) => {
                const currentProgram = res?.payload?.data.filter(program => program.programID == id)
                setProgramLength(currentProgram[0]?.programLength)
                setProgramName(currentProgram[0]?.programName)
                setProgramNotes(currentProgram[0]?.programNotes)
            })

        const payload = {
            programID
        }

        dispatch(getAllProgramEvents(payload))
        dispatch(getEventsForBuildingProgram())
    }, [])

    // will repopulate date in add or edit modal after returning from 
    // event editor page
    useEffect(() => {
        if (eventSnapshot.active) {
            if (eventSnapshot.editEvent) {
                let val = eventSnapshot.event;
                let type = eventSnapshot.event.type;
                setDay(val.day);
                setProgramEventsID(val.programEventsID)
                setEventInEditor({ ...val, type })
                setShowEdit(true);
            } else {
                setProgramID(eventSnapshot.programID)
                setShowAddEvent(true);
                setDaySelected(eventSnapshot.daySelected)
            }
            setEventID(eventSnapshot.eventID)
            setEventType(eventSnapshot.eventType)
            setFilteredValue({ value: eventSnapshot.eventID, label: eventSnapshot.eventName })
            dispatch(updateEventSnapshot({ active: false, editEvent: false }))
        }
    }, [eventSnapshot])


    useEffect(() => {
        const update = []
        for (var i = 0; i <= (programLength - 1); i++) {
            update.push({ id: (i + 1).toString() })
        }
        setDaysArray(update)
    }, [programLength])

    console.log(programEventsState)


    const showFavoriteDetails = (eventId) => {

        const payload = {
            eventID: eventId ? eventId : eventID,
            eventType
        }
        setFavoriteDetailsLoading(true)
        dispatch(getFavoriteDetails(payload))
            .then(res => setFavoriteDetailsLoading(false))
    }

    const editProgramLengthFun = (newlength) => {

        const payload = {
            programLength,
            programName,
            programNotes,
            programID,
            newlength
        }

        if (programBuilt == true) {
            const arr = []
            for (var i = 0; i <= (newlength - 1); i++) {
                arr.push({ id: (i + 1).toString() })
                setDaysArray(arr)
            }
            dispatch(editProgramBuildLength(payload))
        }
    }

    const editProgramNameFun = (newName) => {
        const payload = {
            programLength,
            newName,
            programNotes,
            programID,

        }
        dispatch(editProgramBuildName(payload))
    }

    const editProgramNotesFun = (newNotes) => {
        const payload = {
            programLength,
            programName,
            programNotes,
            programID,
            newNotes
        }
        dispatch(editProgramBuildNotes(payload))
    }

    const submitHandler = (e) => { e.preventDefault(); }

    const repeatFunction = () => {
        if (repeat == "daily") {
            let freq = repFreq === "custom" ? customDayString : repFreq
            const programDays = dayRange(daySelected, untilDay, freq)
            const eventDaysArr = programDays?.map(day => day.toString())
            setEventDays(eventDaysArr)
        } else if (repeat == "none") {
            setEventDays([daySelected])
        } else if (repeat == "weekly") {
            setEventDays(generateWeeklyDays(checkboxArr, daySelected, parseInt(untilDay)))
        }
        //reset checkbox arrray
        setCheckboxArr([])
    }

    const submitHandlerEvent = (e) => {
        e.preventDefault();
        const payload = {
            eventDays,
            programID,
            eventType,
            eventID,
            sendAt,
            is24Hour,
            messageBody
        }

        if (eventType == "message") {
            dispatch(addMessageToProgram(payload))
                .then((res) => {
                    handleCloseAddEvent();
                })
                .then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
        } else if (eventType == "workout" && (repeat !== 'none' && !untilDay)) {
            setShowAddEvent(true)
        } else {
            setUntilDay(false)
            setAlertMessage(false)
            dispatch(addEventToProgram(payload))
                .then((res) => {
                    handleCloseAddEvent();
                })
                .then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
        }
    }

    const submitClients = (e) => {
        e.preventDefault();
        const payload = {
            startDate,
            clientList,
            programID
        }

        dispatch(assignProgramToClients(payload))
    }

    const editEventFunction = (e) => {
        e.preventDefault();
        if (!eventID && eventType !== 'message') {
            window.alert(`Please, choose a ${eventType}.`)
            return
        }

        const payload = {
            day,
            programID,
            eventType,
            eventID,
            programEventsID,
            sendAt,
            is24Hour,
            messageBody
        }

        console.log(eventID, "EVENTID")
        /*
            Control flow to handle cases like switching from an event (workout/task/nutrion)
            to a message
        */
        //handle cases where we are not switching between

        if (
            //types are same OR
            eventInEditor.type === eventType ||
            //we are switching from one standard event type to another
            //ie [workout,task,nutrion] ==> [workout,task,nutrition]
            (
                //event being editted is a standard event type AND
                eventTypes.includes(eventInEditor.type) &&
                //new event type is a standard event type
                eventTypes.includes(eventType)
            )
        ) {
            //handle messages
            if (eventType === 'message') {
                dispatch(updateProgramMessage({
                    is_24_hour_time: is24Hour,
                    program_id: programID,
                    program_message_id: eventInEditor.program_message_id,
                    message_body: messageBody
                }))
                    .then(() => {
                        dispatch(getAllProgramEvents(payload))
                    })
            } else {
                //handle standard event types
                dispatch(editProgramEvent(payload))
                    .then(() => {
                        dispatch(getAllProgramEvents(payload))
                    })
            }
        } else {
            //handle switching between message & standard event type
            //if new event type is a message delete the existing event
            //and add message else vice versa
            if (eventType === 'message') {
                //dispatch delete event
                dispatch(deleteEvent({
                    day: eventInEditor.day,
                    eventType: eventInEditor.type,
                    eventID: eventInEditor.eventID,
                    programEventsID, programID
                }))
                //dispatch add message
                dispatch(addMessageToProgram({
                    eventDays: [eventInEditor.day],
                    programID, sendAt, is24Hour, messageBody
                })).then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
            } else {
                //delete the message
                dispatch(deleteProgramMessage({
                    program_message_id: eventInEditor.program_message_id
                }))
                //save new event
                dispatch(addEventToProgram({
                    ...payload,
                    eventDays: [payload.day]
                }))
                    .then(() => {
                        dispatch(getAllProgramEvents({
                            ...payload,
                            eventDays: [payload.day]
                        }))
                    })
            }
        }
        // console.log(eventInEditor,payload)

        handleCloseEdit();
    }

    const setEventToEdit = (val, type) => {
        setDay(val.day);
        setFilteredValue({ value: val.eventID, label: val.eventName })
        setEventID(val.eventID);
        setProgramEventsID(val.programEventsID)
        setEventInEditor({ ...val, type })
        setEventType(type)
    };

    const handleDelete = () => {
        const payload = {
            day,
            programID,
            eventType,
            eventID,
            programEventsID,
        }
        if (eventType === 'message') {
            dispatch(deleteProgramMessage({
                program_message_id: eventInEditor.program_message_id
            }))
                .then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
        } else {
            dispatch(deleteEvent(payload))
                .then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
        }

        handleCloseEdit();
    }

    const handleDeleteAll = () => {
        const payload = {
            programID,
            eventType,
            eventID,
            programEventsID
        }
        if (eventType === 'message') {
            dispatch(deleteProgramMessageGroup({
                program_message_group_id: eventInEditor.program_message_group_id
            }))
                .then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
        } else {
            dispatch(deleteAllEvents(payload))
                .then(() => {
                    dispatch(getAllProgramEvents(payload))
                })
        }
        handleCloseEdit();
    }

    const handleSaveEvent = () => {
        if (repeat !== 'none' && !untilDay) {
            // window.alert('Please select a day when this repeat event should end.')
            setAlertMessage(true)
            setShowAddEvent(true)
            return
        } else {
            setShowAddEvent(false)
        }
        //handleCloseAddEvent();
        setAlertMessage(false)
        repeatFunction();
        // setUntilDay(false)
    }

    const handleWeeklyDayCheck = (e) => {
        const val = parseInt(e.target.value)
        if (e.target.checked) {
            setCheckboxArr([...checkboxArr, val])
        } else {
            setCheckboxArr([...checkboxArr].filter(v => v !== val))
        }
    }

    //handle state for message input
    //selections
    const messageRef = useRef()

    const [selectionStart, setSelectionStart] = useState(0)

    messageRef?.current?.addEventListener('keyup', e => {
        setSelectionStart(e.target.selectionStart)
    })

    const addAutoMessageTag = (tagType) => {
        const tag = `[${tagType}]`;
        const bodyUpdate = `${messageBody.substring(0, selectionStart)}${tag}${messageBody.substring(selectionStart)}`
        setSelectionStart(selectionStart + tag.length)
        setMessageBody(bodyUpdate)
        messageRef.current.focus()
    }

    const onDragStart = (e, eventObject, eventType) => {
        console.log('event: ', eventObject)
        setDragInfo([eventObject, eventType])
    }

    const onDragOver = (e, day) => {
        e.preventDefault()
    }

    const onDrop = (e, day) => {
        // dispatch(updateProgramMessage({
        // is_24_hour_time: is24Hour,
        // program_id: programID,
        // program_message_id: eventInEditor.program_message_id,
        // message_body: messageBody
        // }))

        // day: 2
        // is_24_hour_time: null
        // message_body: "Hi[fullName][firstName][fullNam"
        // message_template_id: 215
        // program_id: 1367
        // program_message_group_id: 900
        // program_message_id: 1843
        // send_at: "04:00:00"
        // user_id_number: 15343
        const payload = {
            day: day,
            programID: urlParams.get('program'),
            eventType: dragInfo[1],
            eventID: dragInfo[0].eventID,
            programEventsID: dragInfo[0].programEventsID
        }

        if (dragInfo[1] === "message") {
            setIsLoading(true)
            dispatch(updateProgramMessage({
                is_24_hour_time: is24Hour,
                program_id: programID,
                program_message_id: dragInfo[0].program_message_id,
                message_body: dragInfo[0].message_body,
                day: day
            })).then(() => {
                dispatch(getAllProgramEvents(payload)).then(() => {
                    setIsLoading(false)
                })
            })
            console.log("dragInfo: ", dragInfo)
        } else {
            setIsLoading(true)
            dispatch(editProgramEvent(payload)).then(() => {
                dispatch(getAllProgramEvents(payload)).then(() => {
                    setIsLoading(false)
                })
            })
        }
    }

    const toggleCopyModal = () => {
        dispatch(setCopyModal({
            program_id: id,
            show: true
        }))
    }

    function handleRepeatChange(e) {
        //reset "repeat until day" value to program length if changing from no repeat to day or weekly repeat
        if (repeat === "none") {
            setUntilDay(programLength)
        }
        setRepeat(e.target.value);
    }

    const [showUpdateAssignmentModal, setShowUpdateAssignmentModal] = useState(false)
    const toggleUpdateAssignmentModal = () => {
        setShowUpdateAssignmentModal(!showUpdateAssignmentModal)
    }


    const [filteredValue, setFilteredValue] = useState(null)
    function renderEventSelectOptions() {
        return (<>

            <Form.Group >

                <Select
                    className="w-100"
                    placeholder={`Choose a ${eventType}`}
                    value={filteredValue}
                    options={favoritesFiltered.map(event => (
                        { value: event[`${eventType}_id`], label: event.note }
                    ))}
                    onChange={(seletedEvent) => {
                        setFilteredValue(seletedEvent)
                        setEventID(seletedEvent.value);
                        showFavoriteDetails(seletedEvent.value);
                    }}
                />

            </Form.Group>
        </>)
    }



    return (
        <div id="containerDiv" className={classes.mobileContainerDiv}>
            <h1 style={{ lineHeight: '1.5' }}>Program</h1>
            <hr />
            <div style={{ fontSize: "18px" }}>
               
                    <Link to={`/programs?${trainerParam}`}>Program List</Link> / { programName }
            </div>
            <hr />
            {
                status === 'pending' ?
                    <div className="d-flex justify-content-center align-items-center p-5 flex-column">
                        <strong>Loading program details...</strong>
                        <Spinner animation="border" />
                    </div>
                    :
                    <>
                        <Toast onClose={() => setShowMainToast(false)} show={showMainToast} delay={2000} autohide style={{ color: 'white', backgroundColor: '#F5365C' }}>
                            <Toast.Body><strong>Please fill in all required fields</strong></Toast.Body>
                        </Toast>
                        {
                            programEventsState.status == "assigned" && (
                                <Toast onClose={() => setShowAssignToast(false)} show={showAssignToast} delay={2000} autohide style={{ color: 'white', backgroundColor: '#4FD69C' }}>
                                    <Toast.Body><strong>Program successfully assigned</strong></Toast.Body>
                                </Toast>
                            )
                        }

                        {!isOwner &&
                            <Alert variant="info">
                                <i className="fas fa-info-circle mr-2" />
                                <span className="text-dark">
                                    This is a gym program and can only be edited by the gym owner.
                                    {!isChain && <strong className="ml-2">You can still assign this program to clients</strong>}
                                </span>
                            </Alert>
                        }
                        <Card className={classes.programsBox} style={{ backgroundColor: '#F7F8F9' }}>
                            <Form>
                                <Form.Group as={Row} controlId="programLength">
                                    <Form.Label column sm={2}>
                                        Program Length (Days)
                                    </Form.Label>
                                    <Col sm={10}>
                                        <Form.Control
                                            disabled={!isOwner}
                                            value={programLength}
                                            placeholder="required"
                                            type="number"
                                            min="1"
                                            onChange={(e) => { setProgramLength(e.target.value); editProgramLengthFun(e.target.value) }}
                                        />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="programName">
                                    <Form.Label column sm={2}>
                                        Program Name
                                    </Form.Label>
                                    <Col sm={10}>
                                        <Form.Control
                                            disabled={!isOwner}
                                            value={programName}
                                            type="text"
                                            placeholder="required"
                                            onChange={(e) => { setProgramName(e.target.value); editProgramNameFun(e.target.value) }}
                                        />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row}>
                                    <Form.Label column sm={2}>
                                        Program Notes
                                    </Form.Label>
                                    <Col sm={10}>
                                        {(programNotes !== "") &&
                                            <span
                                                style={{
                                                    backgroundColor: "transparent",
                                                    border: 0,
                                                    outline: 0,
                                                    padding: "0px 6px 0px 0px",
                                                    position: "absolute",
                                                    top: 0,
                                                    right: "15px",
                                                    height: "100%",
                                                    display: "table-cell",
                                                    verticalAlign: "middle",
                                                    cursor: "pointer"
                                                }}
                                                onClick={(e) => { (document.getElementById("notesForm").value = ""); setProgramNotes("") }}
                                            >&#x2612;</span>
                                        }
                                        <Form.Control
                                            disabled={!isOwner}
                                            value={programNotes}
                                            placeholder="optional"
                                            as="textarea"
                                            id="notesForm"
                                            rows={2}
                                            onChange={(e) => { setProgramNotes(e.target.value); editProgramNotesFun(e.target.value) }}
                                        />
                                    </Col>

                                </Form.Group>

                                <div className="d-flex align-items-end flex-wrap">
                                    {(isOwner && !isChain) &&
                                        <div className="mr-auto mb-2 mb-md-0">
                                            <small className="text-danger">
                                                <strong>Note</strong>: edits are NOT automatically applied to existing program assignments.
                                            </small>
                                            <br />
                                            <Button
                                                onClick={toggleUpdateAssignmentModal}
                                                variant="outline-dark"
                                                size="sm"
                                            >
                                                Open options for updating existing assignments
                                            </Button>
                                        </div>
                                    }
                                    {(isOwner && user.account_type === 'gym') &&
                                        <Button
                                            variant="outline-primary"
                                            className="mr-1"
                                            onClick={toggleCopyModal}
                                            size="sm"
                                        >
                                            Copy to a Trainer
                                        </Button>
                                    }
                                    {(!isOwner && user.account_type === 'trainer') &&
                                        <Button
                                            variant="outline-primary"
                                            className="mr-1"
                                            onClick={toggleCopyModal}
                                            size="sm"
                                        >
                                            Copy to My Trainer Account
                                        </Button>
                                    }
                                    {!isChain &&
                                        <Button
                                            size="sm"
                                            variant="outline-success"
                                            // type="submit"
                                            onClick={() => handleShowAssign()}

                                        >
                                            Assign To Client
                                        </Button>
                                    }

                                </div>
                            </Form>

                        </Card>
                        {
                            programBuilt == true && (
                                <>
                                    {isOwner &&
                                        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                                            Click on a day to add a workout, task, nutrition, or automated message.
                                        </div>
                                    }
                                    <div style={{ fontSize: "12.8px" }}><i className="fas fa-circle" style={{ color: '#5E72E4' }}>
                                    </i>Workouts | <i className="fas fa-circle" style={{ color: '#3FCE89' }}>
                                        </i>Tasks | <i className="fas fa-circle" style={{ color: '#F66340' }}>
                                        </i>Nutrition | <i className="fas fa-comment-dots" style={{ color: '#43CDEF' }}></i>
                                        <span style={{ marginRight: "10px" }}>Messages</span>
                                        {isLoading &&
                                            <Spinner
                                                animation="border"
                                                role="status"
                                                variant="primary"
                                                style={{ width: "10px", height: "10px" }}
                                            />
                                        }
                                    </div>
                                </>
                            )
                        }

                        <div className={classes.Calendar}>
                            {
                                daysArray.map(({ id }, index) => (
                                    <div
                                        style={{ cursor: isOwner ? 'pointer' : 'auto' }}
                                        onClick={() => {
                                            if (!isOwner) return
                                            handleShowAddEvent(); setDaySelected(index + 1)
                                        }}
                                        className={classes.DayBox}
                                        onDragOver={(e) => onDragOver(e)}
                                        onDrop={(e) => onDrop(e, id)}
                                    >
                                        <div style={{ backgroundColor: '#CED4DA', textAlign: 'center' }}><span>Day {id}</span></div>


                                        <div style={{ fontSize: "12.8px", cursor: "pointer" }}>

                                            {
                                                eventWorkouts?.map(workout => (
                                                    workout?.day == id ? (
                                                        <div style={{ fontSize: "12.8px", cursor: isOwner ? "pointer" : 'auto' }}
                                                            value={workout.programEventsID} onClick={(e) => {
                                                                if (!isOwner) return
                                                                setEventToEdit(workout, 'workout');
                                                                handleShowEdit(e)
                                                            }}
                                                            onDragStart={(e) => {
                                                                if (!isOwner) return
                                                                onDragStart(e, workout, "workout")
                                                            }}
                                                            draggable={isOwner}

                                                        >
                                                            <span><i class="fas fa-circle" style={{ color: '#5E72E4' }}>
                                                            </i>{workout.eventName}</span></div>
                                                    ) : null
                                                ))
                                            }
                                            {
                                                eventNutritions?.map(nut => (
                                                    nut?.day == id ? (
                                                        <div style={{ fontSize: "12.8px", cursor: isOwner ? "pointer" : 'auto' }}
                                                            value={nut.programEventsID} onClick={(e) => {
                                                                if (!isOwner) return
                                                                setEventToEdit(nut, 'nutrition');
                                                                handleShowEdit(e)
                                                            }}
                                                            onDragStart={(e) => {
                                                                if (!isOwner) return
                                                                onDragStart(e, nut, "nutrition")
                                                            }}
                                                            draggable={isOwner}
                                                        >
                                                            <i class="fas fa-circle" style={{ color: '#F66340' }}></i>{nut.eventName}</div>
                                                    ) : null
                                                ))
                                            }
                                            {
                                                eventTasks?.map(task => (
                                                    task?.day == id ? (
                                                        <div style={{ fontSize: "12.8px", cursor: isOwner ? "pointer" : 'auto' }}
                                                            value={task.programEventsID}
                                                            onClick={(e) => {
                                                                if (!isOwner) return
                                                                setEventToEdit(task, 'task');
                                                                handleShowEdit(e)
                                                            }}
                                                            onDragStart={(e) => {
                                                                if (!isOwner) return
                                                                onDragStart(e, task, "task")
                                                            }}
                                                            draggable={isOwner}
                                                        >
                                                            <i class="fas fa-circle" style={{ color: '#3FCE89' }}>
                                                            </i>{task.eventName}</div>
                                                    ) : null
                                                ))
                                            }
                                            {
                                                eventMessages?.map(message => (
                                                    message?.day == id ? (
                                                        <div style={{ fontSize: "12.8px", cursor: isOwner ? "pointer" : 'auto' }}
                                                            value={message.program_message_id}
                                                            onClick={(e) => {
                                                                if (!isOwner) return
                                                                setEventToEdit(message, 'message');
                                                                setMessageBody(message.message_body); handleShowEdit(e)
                                                            }}
                                                            onDragStart={(e) => onDragStart(e, message, "message")}
                                                            draggable={isOwner}
                                                        >
                                                            <i class="fas fa-comment-dots" style={{ color: '#43CDEF' }}>
                                                            </i>{message.message_body}</div>
                                                    ) : null
                                                ))
                                            }
                                        </div>

                                    </div>
                                ))
                            }
                        </div>
                    </>
            }

            {/* Update assignments modal */}
            <UpdateAssignmentModal
                toggleShow={toggleUpdateAssignmentModal}
                show={showUpdateAssignmentModal}
                id={id}
            />

            {/* Add modal */}
            <Modal show={showAddEvent} onHide={handleCloseAddEvent} style={{ padding: "20px" }}>
                <Modal.Header closeButton>
                    <Modal.Title className="font-weight-normal">Add event to program on <b> Day {daySelected} </b></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Toast onClose={() => setShowModalToast(false)} show={showModalToast} delay={2000}
                        autohide style={{ color: 'white', backgroundColor: '#F5365C' }}>
                        <Toast.Body><strong>Please fill in all required fields</strong></Toast.Body>
                    </Toast>
                    <Form onSubmit={submitHandlerEvent}>
                        <Form.Group controlId="eventType">
                            <Form.Label>Event Type</Form.Label>
                            <Form.Control
                                as="select"
                                value={eventType}
                                onChange={(e) => {
                                    setEventType(e.target.value)
                                    setEventID("0");
                                    setFilteredValue(null)
                                    setShowDetails(false);
                                }}
                            >
                                <option value="workout">Workout</option>
                                <option value="nutrition">Nutrition</option>
                                <option value="task">Task</option>
                                <option value="message">Message</option>

                            </Form.Control>
                        </Form.Group>
                        {eventType !== "message" && <>

                            {renderEventSelectOptions()}


                            {eventID === "0" ?
                                <Link
                                    onClick={() => {
                                        const payload = {
                                            programID,
                                            eventType,
                                            daySelected,
                                            active: true,
                                        }

                                        dispatch(saveEventSnapshot(payload))
                                    }}
                                    to={
                                        //long url with dynamic paramters
                                        `/eventCreator/?eventType=${eventType}&client=favorite&origin=program${id}&${trainerParam}`
                                    }
                                >
                                    <p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }}>
                                        Create New {capitalize(eventType)}
                                    </p>
                                </Link> :
                                <div className="d-flex">
                                    <Link
                                        className="mr-2"
                                        onClick={() => {
                                            const payload = {
                                                programID,
                                                eventType,
                                                daySelected,
                                                active: true,
                                            }
                                            dispatch(saveEventSnapshot(payload))
                                        }}
                                        to={
                                            `/eventEditor/?event_id=${eventID}&event_type=AddOrEdit&eventType=${eventType}&client=favorite&origin=program${id}&${trainerParam}`
                                        }
                                    >
                                        <p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }}>
                                            Edit {capitalize(eventType)}
                                        </p>
                                    </Link>
                                    <div> |  </div>
                                    <p className="text-primary ml-2" style={{ cursor: "pointer", fontWeight: "bold" }}
                                        onClick={() => { showFavoriteDetails(); setShowDetails((prev) => !prev) }}>
                                        {

                                            (showDetails == false) ? (
                                                <>
                                                    View {capitalize(eventType)} Details
                                                </>
                                            ) : (
                                                <>
                                                    Hide {capitalize(eventType)} Details
                                                </>
                                            )

                                        }

                                    </p>


                                </div>
                            }
                        </>}

                        {showDetails && !favoriteDetailsLoading &&
                            <EventDetails
                                event={favoriteDetails}
                                eventType={eventType}
                            />
                        }


                        {showDetails && favoriteDetailsLoading &&
                            <Spinner animation="border" role="status" variant="primary" ></Spinner>}

                        {eventType == "message" && (
                            <>
                                {
                                    showMessageTemplate == false ? (
                                        <Form.Group>
                                            <Form.Label>
                                                Message Body
                                            </Form.Label>
                                            <br />
                                            <Button
                                                size="sm"
                                                className="text-primary"
                                                style={{
                                                    backgroundColor: "white",
                                                    marginBottom: 10,
                                                    fontWeight: "bold",
                                                    padding: "5px",
                                                    marginRight: "5px"
                                                }}
                                                onClick={() => addAutoMessageTag("fullName")}
                                            >
                                                Full Name
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="text-primary"
                                                style={{
                                                    backgroundColor: "white",
                                                    marginBottom: 10,
                                                    fontWeight: "bold",
                                                    padding: "5px",
                                                    marginRight: "5px"
                                                }}
                                                onClick={() => addAutoMessageTag("firstName")}
                                            >
                                                First Name
                                            </Button>
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip id="message">
                                                        Replaces [clientName] with recipient's user name
                                                    </Tooltip>
                                                }
                                            >
                                                <i class="fas fa-info-circle"></i>
                                            </OverlayTrigger>
                                            <Form.Control
                                                ref={messageRef}
                                                placeholder="Optional"
                                                as="textarea"
                                                rows={2}
                                                value={messageBody}
                                                onClick={e => {
                                                    setSelectionStart(e.target.selectionStart)
                                                }}
                                                onChange={(e) => setMessageBody(e.target.value)}
                                            />
                                        </Form.Group>
                                    ) :
                                        (
                                            <Form.Group>
                                                <Form.Label>
                                                    Use Previous Message
                                                </Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    onChange={(e) => { setMessageBody(e.target.value); setShowMessageTemplate(false) }}>

                                                    <option>Select Message...</option>
                                                    {
                                                        templates?.map(template => (
                                                            <option value={template?.message_body} >{template?.message_body}</option>

                                                        ))
                                                    }
                                                </Form.Control>

                                            </Form.Group>

                                        )
                                }
                                {
                                    showMessageTemplate == false && (
                                        <p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }} onClick={() => { dispatch(getMessageTemplates()); setShowMessageTemplate(prev => !prev) }}>
                                            Use previous message
                                        </p>
                                    )
                                }
                                <Form.Group>
                                    <Form.Label>
                                        Time
                                    </Form.Label>
                                    <Form.Control
                                        as="select"
                                        onChange={(e) => setSendAt(e.target.value)}
                                    >
                                        <option>Select...</option>
                                        <option value={"00:00:00"}>12:00 AM</option>
                                        <option value={"01:00:00"}>1:00 AM</option>
                                        <option value={"02:00:00"}>2:00 AM</option>
                                        <option value={"03:00:00"}>3:00 AM</option>
                                        <option value={"04:00:00"}>4:00 AM</option>
                                        <option value={"05:00:00"}>5:00 AM</option>
                                        <option value={"06:00:00"}>6:00 AM</option>
                                        <option value={"07:00:00"}>7:00 AM</option>
                                        <option value={"08:00:00"}>8:00 AM</option>
                                        <option value={"09:00:00"}>9:00 AM</option>
                                        <option value={"10:00:00"}>10:00 AM</option>
                                        <option value={"11:00:00"}>11:00 AM</option>
                                        <option value={"12:00:00"}>12:00 PM</option>
                                        <option value={"13:00:00"}>1:00 PM</option>
                                        <option value={"14:00:00"}>2:00 PM</option>
                                        <option value={"15:00:00"}>3:00 PM</option>
                                        <option value={"16:00:00"}>4:00 PM</option>
                                        <option value={"17:00:00"}>5:00 PM</option>
                                        <option value={"18:00:00"}>6:00 PM</option>
                                        <option value={"19:00:00"}>7:00 PM</option>
                                        <option value={"20:00:00"}>8:00 PM</option>
                                        <option value={"21:00:00"}>9:00 PM</option>
                                        <option value={"22:00:00"}>10:00 PM</option>
                                        <option value={"23:00:00"}>11:00 PM</option>
                                    </Form.Control>

                                    <small>
                                        These are sent to clients in their timezone.
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip id="message">
                                                    Ex: A morning reminder will be sent in the morning of each recipient whether they are in Australia or Europe
                                                </Tooltip>
                                            }
                                        >
                                            <i class="fas fa-info-circle"></i>
                                        </OverlayTrigger>

                                    </small>
                                </Form.Group>

                            </>)
                        }

                        <Form.Group controlId="exampleForm.ControlSelect1">
                            <Form.Label>Repeat</Form.Label>
                            <Form.Control
                                as="select"
                                onChange={(e) => handleRepeatChange(e)}
                            >
                                <option value={"none"}>none</option>
                                <option value={"daily"}>daily</option>
                                <option value={"weekly"}>weekly</option>

                            </Form.Control>
                        </Form.Group>
                        {
                            repeat == "daily" ? (
                                <>
                                    <Form.Group controlId="exampleForm.ControlSelect1">
                                        <Form.Label>Repeat Until Day</Form.Label>
                                        <Form.Control
                                            placeholder="required"
                                            type="number"
                                            value={untilDay}
                                            onChange={(e) => setUntilDay(e.target.value)}
                                        >
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group controlId="exampleForm.ControlSelect1">

                                        <Form.Label className="mt-2">Repeat Frequency </Form.Label>
                                        <Form.Control as={"select"}
                                            placeholder="required"
                                            type="number"
                                            value={repFreq}
                                            onChange={(e) => {
                                                setRepFreq(e.target.value)

                                            }}
                                        >
                                            <option value="1"> Every Day </option>
                                            <option value="2"> Every 2nd Day </option>
                                            <option value="3"> Every 3rd Day </option>
                                            <option value="4"> Every 4th Day </option>
                                            <option value="5"> Every 5th Day </option>
                                            <option value="6"> Every 6th Day </option>
                                            <option value="7"> Every 7th Day </option>
                                            <option value="8"> Every 8th Day </option>
                                            <option value="9"> Every 9th Day </option>
                                            <option value="10"> Every 10th Day </option>
                                            <option value="custom"> Custom </option>
                                        </Form.Control>

                                        {repFreq === "custom" &&
                                            <Form.Group>
                                                <Form.Label>Custom Repeat Frequency</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    onChange={e => {
                                                        setCustomDayString(e.target.value)
                                                    }}
                                                    placeholder="days"
                                                />
                                            </Form.Group>}
                                    </Form.Group>
                                </>
                            ) : repeat == "weekly" ? (
                                <>
                                    <Form.Group controlId="exampleForm.ControlSelect1">
                                        <Form.Label>Repeat Until Day</Form.Label>
                                        <Form.Control
                                            placeholder="required"
                                            value={untilDay}
                                            type="number"
                                            onChange={(e) => setUntilDay(e.target.value)}
                                        >

                                        </Form.Control>
                                    </Form.Group>
                                    <fieldset

                                    >
                                        <Form.Group>
                                            <Form.Check
                                                type="checkbox"
                                                label="1st Day"
                                                name="weeklyCheckbox"
                                                id="1Day"
                                                value={1}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="2nd Day"
                                                name="weeklyCheckbox"
                                                id="2Day"
                                                value={2}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="3rd Day"
                                                name="weeklyCheckbox"
                                                id="3Day"
                                                value={3}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="4th Day"
                                                name="weeklyCheckbox"
                                                id="4Day"
                                                value={4}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="5th Day"
                                                name="weeklyCheckbox"
                                                id="5Day"
                                                value={5}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="6th Day"
                                                name="weeklyCheckbox"
                                                id="6Day"
                                                value={6}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="7th Day"
                                                name="weeklyCheckbox"
                                                id="7Day"
                                                value={7}
                                                onChange={handleWeeklyDayCheck}
                                            />
                                        </Form.Group>
                                    </fieldset>

                                </>
                            ) : (
                                <p></p>
                            )
                        }
                        {alertMessage && <p style={{ color: "red" }}>Please select a day when this repeat event should end</p>}
                        <Button
                            type="submit"
                            variant={"primary"}
                            style={{
                                //backgroundColor: "#5E72E4",
                                float: "right",
                                border: "none",
                                marginBottom: 10,
                                fontWeight: "bold",
                                padding: "15px"
                            }}
                            onClick={handleSaveEvent}>
                            Save
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEdit} onHide={handleCloseEdit}>
                <Modal.Header closeButton>
                    <Modal.Title className="font-weight-normal">Edit event on <b> Day {day}</b></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={editEventFunction}>
                        <Form.Group controlId="eventType">
                            <Form.Label>Event Type</Form.Label>
                            <Form.Control
                                as="select"
                                defaultValue={eventType}
                                onChange={(e) => {
                                    const newEventType = e.target.value
                                    setEventID(0)
                                    setShowDetails(false);
                                    setEventType(newEventType)
                                    showFavoriteDetails(e.target.value);
                                    setFilteredValue(null)
                                }}

                            >
                                <option value="workout">Workout</option>
                                <option value="nutrition">Nutrition</option>
                                <option value="task">Task</option>
                                <option value="message">Message</option>

                            </Form.Control>
                        </Form.Group>

                        {
                            <>
                                {eventType !== "message" && <>
                                    <Form.Group >

                                        <Select
                                            className="w-100"
                                            placeholder={`Choose a ${eventType}`}
                                            value={filteredValue}
                                            options={favoritesFiltered.map(event => (
                                                { value: event[`${eventType}_id`], label: event.note }
                                            ))}
                                            onChange={(seletedEvent) => {
                                                setFilteredValue(seletedEvent)
                                                setEventID(seletedEvent.value);
                                                showFavoriteDetails(seletedEvent.value);
                                            }}
                                        />

                                    </Form.Group>



                                    <div className="d-flex">
                                        <Link
                                            className=""
                                            onClick={() => {
                                                const payload = {
                                                    programID,
                                                    eventType,
                                                    daySelected,
                                                    active: true,
                                                    editEvent: true,
                                                    event: eventInEditor,
                                                }
                                                dispatch(saveEventSnapshot(payload))
                                            }}
                                            to={
                                                `/eventEditor/?event_id=${eventID}&event_type=AddOrEdit&eventType=${eventType}&client=favorite&origin=program${id}&${trainerParam}`
                                            }
                                        >
                                            <p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }}>
                                                Edit {eventType}
                                            </p>
                                        </Link>
                                        <div className="mx-2"> |  </div>
                                        <p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }} onClick={() => { showFavoriteDetails(); setShowDetails((prev) => !prev) }}>
                                            {
                                                showDetails == false ? (
                                                    <>
                                                        View {eventType} Details
                                                    </>
                                                ) : (
                                                    <>
                                                        Hide {eventType} Details
                                                    </>
                                                )
                                            }

                                        </p>
                                    </div>


                                    {
                                        showDetails && !favoriteDetailsLoading &&

                                        <EventDetails
                                            event={favoriteDetails}
                                            eventType={eventType}
                                        />
                                    }

                                    {
                                        showDetails && favoriteDetailsLoading &&
                                        <span>
                                            <Spinner animation="border" role="status" variant="primary" ></Spinner>
                                        </span>
                                    }
                                </>}
                                {eventType == "message" && (
                                    <>
                                        {
                                            showMessageTemplate == false ? (
                                                <Form.Group>
                                                    <Form.Label>
                                                        Message Body
                                                    </Form.Label>
                                                    <br />
                                                    <Button
                                                        size="sm"
                                                        className="text-primary"
                                                        style={{
                                                            backgroundColor: "white",
                                                            marginBottom: 10,
                                                            fontWeight: "bold",
                                                            padding: "5px",
                                                            marginRight: "5px"
                                                        }}
                                                        onClick={() => addAutoMessageTag("fullName")}
                                                    >
                                                        Full Name
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className="text-primary"
                                                        style={{
                                                            backgroundColor: "white",
                                                            marginBottom: 10,
                                                            fontWeight: "bold",
                                                            padding: "5px",
                                                            marginRight: "5px"
                                                        }}
                                                        onClick={() => addAutoMessageTag("firstName")}
                                                    >
                                                        First Name
                                                    </Button>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        overlay={
                                                            <Tooltip id="message">
                                                                Replaces [clientName] with recipient's user name
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i class="fas fa-info-circle"></i>
                                                    </OverlayTrigger>
                                                    <Form.Control
                                                        ref={messageRef}
                                                        placeholder="Optional"
                                                        as="textarea"
                                                        rows={2}
                                                        value={messageBody}
                                                        onClick={e => {
                                                            setSelectionStart(e.target.selectionStart)
                                                        }}
                                                        onChange={(e) => setMessageBody(e.target.value)}
                                                    />
                                                </Form.Group>
                                            ) :
                                                (
                                                    <Form.Group>
                                                        <Form.Label>
                                                            Previous Messages
                                                        </Form.Label>

                                                        <Form.Control
                                                            as="select"
                                                            onChange={(e) => { setMessageBody(e.target.value); setShowMessageTemplate(false) }}>

                                                            <option>Select Message...</option>
                                                            {
                                                                templates?.map(template => (
                                                                    <option value={template?.message_body} >{template?.message_body}</option>

                                                                ))
                                                            }
                                                        </Form.Control>

                                                    </Form.Group>

                                                )
                                        }
                                        {
                                            showMessageTemplate == false && (
                                                <p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }} onClick={() => { dispatch(getMessageTemplates()); setShowMessageTemplate(prev => !prev) }}>
                                                    Use previous message
                                                </p>
                                            )
                                        }
                                        <Form.Group>
                                            <Form.Label>
                                                Time
                                            </Form.Label>
                                            <Form.Control
                                                as="select"
                                                onChange={(e) => setSendAt(e.target.value)}
                                            >
                                                <option></option>
                                                <option value={"00:00:00"}>12:00 AM</option>
                                                <option value={"01:00:00"}>1:00 AM</option>
                                                <option value={"02:00:00"}>2:00 AM</option>
                                                <option value={"03:00:00"}>3:00 AM</option>
                                                <option value={"04:00:00"}>4:00 AM</option>
                                                <option value={"05:00:00"}>5:00 AM</option>
                                                <option value={"06:00:00"}>6:00 AM</option>
                                                <option value={"07:00:00"}>7:00 AM</option>
                                                <option value={"08:00:00"}>8:00 AM</option>
                                                <option value={"09:00:00"}>9:00 AM</option>
                                                <option value={"10:00:00"}>10:00 AM</option>
                                                <option value={"11:00:00"}>11:00 AM</option>
                                                <option value={"12:00:00"}>12:00 PM</option>
                                                <option value={"13:00:00"}>1:00 PM</option>
                                                <option value={"14:00:00"}>2:00 PM</option>
                                                <option value={"15:00:00"}>3:00 PM</option>
                                                <option value={"16:00:00"}>4:00 PM</option>
                                                <option value={"17:00:00"}>5:00 PM</option>
                                                <option value={"18:00:00"}>6:00 PM</option>
                                                <option value={"19:00:00"}>7:00 PM</option>
                                                <option value={"20:00:00"}>8:00 PM</option>
                                                <option value={"21:00:00"}>9:00 PM</option>
                                                <option value={"22:00:00"}>10:00 PM</option>
                                                <option value={"23:00:00"}>11:00 PM</option>
                                            </Form.Control>

                                            <small>
                                                These are sent to clients in their timezone.
                                                <OverlayTrigger
                                                    placement="right"
                                                    overlay={
                                                        <Tooltip id="message">
                                                            Ex: A morning reminder will be sent in the morning of each recipient whether they are in Australia or Europe
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i class="fas fa-info-circle"></i>
                                                </OverlayTrigger>

                                            </small>
                                        </Form.Group>
                                    </>
                                )}
                                <div className="mt-5">
                                    <Button

                                        size="sm"
                                        variant={"primary"}
                                        style={{
                                            float: "right",
                                            marginBottom: 10,
                                            padding: "8px",
                                            margin: "5px"
                                        }}
                                        type="submit"
                                    >
                                        Save Changes
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={"outline-danger"}
                                        style={{
                                            float: "right",
                                            marginBottom: 10,
                                            padding: "8px",
                                            margin: "5px"
                                        }}
                                        onClick={() => handleDelete()}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="danger"
                                        style={{
                                            float: "right",
                                            marginBottom: 10,
                                            padding: "8px",
                                            margin: "5px"
                                        }}
                                        onClick={() => { handleDeleteAll(); }}
                                    >
                                        Delete All
                                    </Button>
                                </div>
                            </>
                        }
                    </Form>
                </Modal.Body>
            </Modal>
            <CopyModal />

            <AssignModal />
        </div >
    )
}

export default ProgramsEdit
